import { useDispatch } from 'react-redux';
import { useMutation } from '@tanstack/react-query';
import { QUERY_KEY_MOVE_FILES_LIST, FILES_QUERY_KEY } from 'Data/keys/QueryKeys';
import { IMoveFileRequest } from 'Data/interfaces/ListagemArquivos/IMoveFileRequest';
import { IUseQueryFilesReturn } from 'Data/interfaces/ListagemArquivos/IListFiles';
import ObrasApi from 'Data/Obras.api';
import { queryClient } from 'Context/QueryClientContext';
import toastHandler from 'Utils/toastHandler';

export const useQueryMoveFileMove = () => {
  const { mutate, isPending } = useMutation({
    mutationFn: (variables: IMoveFileRequest) => {
      return ObrasApi.moveFile(variables);
    },
    onMutate: async (variables) => {
      const previousOldFiles = queryClient.getQueryData<IUseQueryFilesReturn>([
        FILES_QUERY_KEY,
        Number(variables.ConstructionSiteId),
        variables.CurrentFolderId,
      ]);
      const previousCurrentFolderList = queryClient.getQueryData<IUseQueryFilesReturn>(
        [QUERY_KEY_MOVE_FILES_LIST, Number(variables.ConstructionSiteId), variables.CurrentFolderId],
      );

      queryClient.setQueryData<IUseQueryFilesReturn>(
        [
          FILES_QUERY_KEY,
          Number(variables.ConstructionSiteId),
          variables.CurrentFolderId,
        ],
        (old) => {
          if (old) {
            if (old.ListFiles.length > 0) {
              return {
                ...old,
                ListFiles: old.ListFiles.filter((file) => {
                  return !variables.FilesId.includes(file.Identifier.ApiId);
                }),
              };
            }
            return { ...old };
          }
          return old;
        },
      );
      queryClient.setQueryData<IUseQueryFilesReturn>(
        [QUERY_KEY_MOVE_FILES_LIST, Number(variables.ConstructionSiteId), variables.CurrentFolderId],
        (old) => {
          if (old) {
            return {
              ...old,
              ListFiles: old.ListFiles.filter((file) => {
                return !variables.FilesId.includes(file.Identifier.ApiId);
              }),
            };
          }
          return old;
        },
      );

      return { previousOldFiles, previousCurrentFolderList };
    },
    onSuccess: async (_data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [
            FILES_QUERY_KEY,
            Number(variables.ConstructionSiteId),
            variables.NewFolderId,
          ],
          exact: true,
        }),
        queryClient.invalidateQueries({
          queryKey: [
            QUERY_KEY_MOVE_FILES_LIST,
            Number(variables.ConstructionSiteId),
            variables.NewFolderId,
          ],
          exact: true,
        }),
      ]);
    },
    onError: async (error: any, variables, context) => {
      await Promise.all([
        await queryClient.cancelQueries({
          queryKey: [
            FILES_QUERY_KEY,
            Number(variables.ConstructionSiteId),
            variables.CurrentFolderId,
          ],
        }),
        await queryClient.cancelQueries({
          queryKey: [
            QUERY_KEY_MOVE_FILES_LIST,
            Number(variables.ConstructionSiteId),
            variables.CurrentFolderId,
          ],
          exact: true,
        }),
      ]);

      queryClient.setQueryData<IUseQueryFilesReturn>(
        [
          FILES_QUERY_KEY,
          Number(variables.ConstructionSiteId),
          variables.CurrentFolderId,
        ],
        context?.previousOldFiles,
      );
      queryClient.setQueryData<IUseQueryFilesReturn>(
        [QUERY_KEY_MOVE_FILES_LIST, Number(variables.ConstructionSiteId), variables.CurrentFolderId],
        context?.previousCurrentFolderList,
      );

      const msg = 'Erro ao mover arquivo';
      const errorMsg = error.errors ? error.errors[0]?.Message || msg : msg;
      toastHandler.showError(errorMsg);
    },
  });

  return {
    moveFile: mutate,
    isLoading: isPending,
  };
};
